import {
  Typography,
  Paper,
  TextField,
  Box,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import CollapsibleFormElement from "../../common/collapsible-form-element";
import {
  LARA_TEMPLATE_VARIABLES,
} from "../../../pages/playground/types";

export default function LaraInstructions({
  instructions,
  setInstructions,
}: {
  instructions: string[];
  setInstructions: (instructions: string[]) => void;
}) {
  const [textValue, setTextValue] = useState(() => instructions.join("\n"));

  // Sync local state when instructions prop changes externally
  useEffect(() => {
    setTextValue(instructions.join("\n"));
  }, [instructions]);

  // Parse text into instructions array - handle both \n and \r\n
  const parseInstructions = (text: string): string[] => {
    return text
      .split(/\r?\n/)  // Handle both Unix and Windows line endings
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextValue(event.target.value);
  };

  const handleBlur = () => {
    setInstructions(parseInstructions(textValue));
  };

  const handleVariableChipClick = (variableName: string) => {
    const insertText = `{${variableName}}`;
    const newText = textValue + (textValue && !textValue.endsWith("\n") ? "\n" : "") + insertText;
    setTextValue(newText);
    setInstructions(parseInstructions(newText));
  };

  const instructionCount = parseInstructions(textValue).length;

  return (
    <CollapsibleFormElement title="AI Translation Instructions">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        Add custom instructions for the AI translation. Each line will be
        treated as a separate instruction. Use template variables like{" "}
        <code style={{ backgroundColor: "#f5f5f5", padding: "2px 4px" }}>
          {"{garment_type}"}
        </code>{" "}
        or{" "}
        <code style={{ backgroundColor: "#f5f5f5", padding: "2px 4px" }}>
          {"{color}"}
        </code>{" "}
        to include job-specific bullet points.
      </Typography>

      {/* Available Variables Section */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: "block" }}
        >
          Available bullet points (click to insert):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {LARA_TEMPLATE_VARIABLES.map((variable) => (
            <Chip
              key={variable.name}
              label={`{${variable.name}}`}
              size="small"
              variant="outlined"
              onClick={() => handleVariableChipClick(variable.name)}
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
              title={variable.description}
            />
          ))}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={textValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={`Enter translation instructions (one per line).\nExample: The fabric is {fabric}\nExample: This product is designed for {context_of_use}`}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiInputBase-root": {
              fontFamily: "monospace",
              fontSize: "0.875rem",
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {instructionCount} instruction{instructionCount !== 1 && "s"}{" "}
          configured
        </Typography>
      </Paper>
    </CollapsibleFormElement>
  );
}
